import React, { useState, useEffect, Fragment, useRef } from 'react';
import { Link } from 'react-router-dom';
import { bool, func, string, shape, array, number } from 'prop-types';
import cn from 'classnames';
import { Input, Modal, Toggle } from 'ps-design';
import SlideoutHeader from 'ps-components/Slideout/Header/SlideoutHeader';
import Dropdown from 'ps-components/Dropdown/Dropdown';
import SlideoutFooter from 'ps-components/Slideout/Footer/SlideoutFooter';
import SlideoutFooterStandardButtons from 'ps-components/Slideout/Footer/SlideoutFooterStandardButtons';
import ElementSelection from './ElementSelection';
import ElementList from './ElementList';
import ProgressStatusOverlay from 'ps-components/ProgressStatus/ProgressStatusOverlay';
import ModalHeader from 'ps-components/Modal/ModalHeader/ModalHeader';
import ModalFooter from 'ps-components/Modal/ModalFooter/ModalFooter';
import addAlert from 'ps-components/AlertToast/addAlert';

import './EditView.scss';

function getScopeDropdownOption(scope) {
  switch (scope) {
    case 'global':
      return {
        title: 'Global View',
        text: 'Visible to all sites',
        key: 'global'
      };
    case 'group':
      return {
        title: 'Group View',
        text: 'Only Visible to group users',
        key: 'group'
      };
    case 'site':
      return {
        title: 'Site View',
        text: 'Visible to everyone',
        key: 'site'
      };
    default:
      return {
        title: 'Personal View',
        text: 'Only visible to me',
        key: 'personal'
      };
  }
}

EditView.propTypes = {
  actions: shape({
    saveView: func,
    getElementTree: func,
    getElementTreeReset: func
  }),
  categoryCode: string,
  overrideCategoryCode: string,
  currentViewId: number,
  editMode: bool,
  groupId: number,
  selectedView: shape({
    report_pk: number,
    scope: string,
    view_description: string,
    view_name: string,
    view_pk: number,
    isCopy: bool
  }),
  hideSlideout: func,
  hideAggs: bool,
  maid: string,
  marketPricing: shape({
    elementTree: shape({
      data: array,
      defaultFields: array,
      currentFields: array,
      error: string,
      loading: bool,
      loaded: bool
    }),
    saveView: shape({
      error: string,
      loading: bool
    })
  }),
  onRefresh: func,
  onRefreshData: func,
  organizationId: number,
  page: string,
  pageCode: string,
  pageName: string,
  permissions: shape({
    globalAdmin: bool,
    groupAdmin: bool,
    readOnlyUser: bool,
    regularUser: bool,
    reportViewUser: bool,
    restrictedUser: bool,
    siteAdmin: bool,
    superAdmin: bool
  }),
  setSelectedView: func,
  subsite: string,
  toggleEditView: func,
  includeAggregates: bool,
  groupAdminCanConfigureViews: bool,
  regularUserCanConfigureViews: bool,
  disableGroupInput: func,
  isGroupingEnabled: bool,
  showAggregates: bool,
  useOverrideLabelFormatter: bool,
  elementsFilter: func,
  categoryFilter: func,
  elementCountLimit: number,
  hideRequiredFields: bool
};

export default function EditView(props) {
  const [title, setTitle] = useState(props.selectedView?.view_name || 'Untitled View');
  const [editTitle, setEditTitle] = useState(false);
  const [description, setDescription] = useState(props.selectedView?.view_description || '');
  const [editDescription, setEditDescription] = useState(false);
  const [tempDescription, setTempDescription] = useState(description);
  const [scope, setScope] = useState(props.selectedView?.scope || 'personal');
  const [isDefault, setIsDefault] = useState(props.defaultViewId === props.selectedView?.view_pk || false);
  const [selectedElements, setSelectedElements] = useState([]);
  const [unsavedEdit, setUnsavedEdit] = useState(false);
  const [isReturn, setIsReturn] = useState(true);

  const isReadOnly = !canEditView();

  let countLimitReached = false;
  if (props.elementCountLimit) {
    countLimitReached = (selectedElements.filter(e => !e.mandatory_field)).length >= props.elementCountLimit;
  }
  
  const isRestricted = props.permissions?.restrictedUser;

  const cancelRef = useRef();

  if (isRestricted) return null;

  // can't deconstruct, selectedView can be null
  const reportPk = props.selectedView?.report_pk;
  const viewPk = props.selectedView?.view_pk;

  // if copying view, focus the title on init
  useEffect(() => {
    if (props.selectedView?.isCopy) setEditTitle(true);
  }, []);

  useEffect(() => {
    props.actions.getElementTree(props.maid, props.overrideCategoryCode || props.categoryCode, {
      reportPk: props.editMode ? reportPk : null,
      reportElements: true,
      analyticsAggMode: true,
      convertAllToSlotted: props.pageCode === 'slot_job'
    });
  }, [props.categoryCode]);

  useEffect(() => {
    const defaults = props.marketPricing?.elementTree?.defaultFields || [];
    const current = props.marketPricing?.elementTree?.currentFields || [];

    // copy field_metadata into metadata for all currents
    current.forEach(c => {
      if (c.field_metadata) {
        c.metadata = { ...c.field_metadata, ...c.field_column_metadata };
      }
    });

    // If default or current fields changes, rebuild the selectedElementsArray
    let newSelected = [...current];

    // Add defaults to the top of the list, if they don't exist already
    if (defaults) {
      for (let i = defaults.length - 1; i >= 0; i--) {
        if (defaults[i].field_pk) {
          const foundField = newSelected.find(s => s.field_pk === defaults[i].field_pk);
          if (!foundField) {
            newSelected = [defaults[i], ...newSelected];
          } else {
            foundField.mandatory_field = true;
          }
        }
      }
    }

    setSelectedElements(newSelected);
  }, [props.marketPricing?.elementTree?.defaultFields, props.marketPricing?.elementTree?.currentFields]);

  const scopeDropdownItems = getScopeDropdownItems();

  function canEditView() {
    const {
      globalAdmin,
      groupAdmin,
      readOnlyUser,
      reportViewUser,
      restrictedUser,
      siteAdmin,
      superAdmin
    } = props.permissions;
    const { groupAdminCanConfigureViews, regularUserCanConfigureViews } = props;

    // read-only users can edit and create their own personal views
    if (readOnlyUser) {
      return scope === 'personal';
    }

    if (reportViewUser || restrictedUser) return false;

    const groupOrAbove = groupAdmin || siteAdmin || globalAdmin || superAdmin;
    const siteOrAbove = siteAdmin || globalAdmin || superAdmin;
    const globalAdminOrAbove = globalAdmin || superAdmin;
    const isGroupSameAsOrg = props.groupId === props.organizationId;

    if (!groupOrAbove && !regularUserCanConfigureViews) {
      return false;
    }

    if (!siteOrAbove && groupOrAbove && !groupAdminCanConfigureViews) {
      return false;
    }

    if (scope === 'personal') return true;
    if (scope === 'group' && groupOrAbove && !isGroupSameAsOrg) return true;
    if (scope === 'site' && siteOrAbove) return true;
    if (scope === 'global' && globalAdminOrAbove && props.subsite === 'marketpaymaster') return true;

    return false;
  }

  function getScopeDropdownItems() {
    const {
      globalAdmin,
      groupAdmin,
      readOnlyUser,
      reportViewUser,
      restrictedUser,
      siteAdmin,
      superAdmin
    } = props.permissions;

    // read-only users can edit and create their own personal views
    if (readOnlyUser) {
      if (scope === 'personal') return [getScopeDropdownOption('personal')];

      return [];
    }

    if (reportViewUser || restrictedUser) return [];

    const groupOrAbove = groupAdmin || siteAdmin || globalAdmin || superAdmin;
    const siteOrAbove = siteAdmin || globalAdmin || superAdmin;
    const isGroupSameAsOrg = props.groupId === props.organizationId;

    const options = [getScopeDropdownOption('personal')];

    if (groupOrAbove && !isGroupSameAsOrg) {
      options.push(getScopeDropdownOption('group'));
    }

    if (siteOrAbove) {
      options.push(getScopeDropdownOption('site'));
    }

    return options;
  }

  function toggleSelectedElement(element) {
    if (element) {
      const id = element.field_pk;
      const aggregateCode = element.aggregate_code;

      let elementIndex;
      if (aggregateCode) {
        elementIndex = selectedElements.findIndex(e => e.field_pk === id && e.aggregate_code === aggregateCode);
      } else {
        elementIndex = selectedElements.findIndex(e => e.field_pk === id);
      }

      const newElements = [...selectedElements];

      if (elementIndex > -1) {
        newElements.splice(elementIndex, 1);
      } else {
        if (countLimitReached) {
          return addAlert('You have reached the maximum number of elements allowed.', 'warning');
        }
        // If element being added is a numeric, make it avg
        if (props.includeAggregates && ['monetary', 'percent', 'number'].includes(element.field_type_code)) {
          element.aggregate_code = 'avg';
        }

        newElements.push(element);
      }

      setSelectedElements(newElements);
      setUnsavedEdit(true);
    }
  }

  function toggleSelectedAggType(element, aggregateType, checked) {
    if (element) {
      const id = element.field_pk;
      const newElements = [...selectedElements];

      if (checked) {
        if (countLimitReached) return addAlert('You have reached the maximum number of elements allowed.', 'warning');
        if (!selectedElements.find(e => e.field_pk === id && e.aggregate_code === aggregateType)) {
          // Add copy of element with new agg code
          const existingElementIndex = selectedElements.findIndex(e => e.field_pk === id);
          const newElement = {
            ...newElements[existingElementIndex],
            aggregate_code: aggregateType
          };

          // Place new element right next to old one
          newElements.splice(existingElementIndex + 1, 0, newElement);
        }
      } else {
        // Remove given agg type from element list (unless it's the last)
        // Add copy of element with new agg code
        const existingElementIndex = selectedElements.findIndex(
          e => e.field_pk === id && e.aggregate_code === aggregateType
        );

        newElements.splice(existingElementIndex, 1);
      }

      setSelectedElements(newElements);
      setUnsavedEdit(true);
    }
  }

  function getScopeText() {
    return getScopeDropdownOption(scope)?.title;
  }

  function onTitleKey(event) {
    if (event.key === 'Enter') {
      setEditTitle(false);
    }
  }

  function onDescriptionKey(event) {
    if (event.key === 'Enter') {
      setEditDescription(false);
    } else if (event.key === 'Escape') {
      setEditDescription(false);
      setDescription(tempDescription);
    }
  }

  function startEditDescription() {
    setTempDescription(description);
    setEditDescription(true);
  }

  function onReturn() {
    // show confirmation modal if elements have changed
    if (unsavedEdit) {
      setIsReturn(true);
      cancelRef?.current?.show();
    } else {
      confirmCancelReturn();
    }
  }

  function closeSlideout() {
    // show confirmation modal if elements have changed
    if (unsavedEdit) {
      setIsReturn(false);
      cancelRef?.current?.show();
    } else {
      confirmCancel();
    }
  }

  function confirmCancelReturn() {
    props.toggleEditView();
    props.actions.getElementTreeReset();
  }

  function confirmCancel() {
    props.toggleEditView();
    props.actions.getElementTreeReset();
    props.hideSlideout();
  }

  function cancelModal() {
    cancelRef.current.hide();
  }

  function submitModal() {
    isReturn ? confirmCancelReturn() : confirmCancel();
  }

  function changeTitle(id, value) {
    if (title !== value) {
      setUnsavedEdit(true);
      setTitle(value);
    }
  }

  function getTitle() {
    return (
      <div className="view-title">
        {editTitle && !isReadOnly ? (
          <Input
            className="view-title-text --edit"
            id="view-title__edit"
            onChange={changeTitle}
            onKeyDown={onTitleKey}
            onBlur={() => setEditTitle(false)}
            type="text"
            value={title}
            autoFocus={editTitle}
          />
        ) : (
          <div className="view-title-text">{title}</div>
        )}

        {!isReadOnly && <i className="icon-edit-title icon-pencil" onClick={() => setEditTitle(!editTitle)} />}
      </div>
    );
  }

  function changeDescription(e) {
    if (description !== e.target.value) {
      setUnsavedEdit(true);
      setDescription(e.target.value);
    }
  }

  function getDescription() {
    if (isReadOnly) {
      return <div className="view-description--readonly">{description}</div>;
    }

    if (editDescription) {
      return (
        <Fragment>
          <textarea
            id="view-description-textarea"
            className="view-description-textarea"
            onChange={changeDescription}
            onKeyDown={onDescriptionKey}
            onBlur={() => setEditDescription(false)}
            autoFocus={editDescription}
            value={description}
          />

          <div className="view-description__instruction-text">
            <span className="bold">Enter</span>
            <span className="text">save</span>
            <span className="bold">Esc</span>
            <span className="text">cancel</span>
          </div>
        </Fragment>
      );
    }

    return (
      <div className="view-description" onClick={startEditDescription}>
        {description || 'Add Description'}
      </div>
    );
  }

  function applySaveView() {
    if (!isReadOnly) {
      const { maid, page, pageCode, editMode, categoryCode, includeAggregates, selectedView } = props;

      const data = {
        isDefault,
        page,
        pageCode,
        scope,
        viewDescription: description,
        viewName: title,
        reportPk: editMode ? reportPk : null,
        viewPk: editMode ? viewPk : null,
        categoryCode,
        includeAggregates,
        isCopy: selectedView?.isCopy
      };

      props.actions.saveView(maid, selectedElements, data).then(success => {
        if (success) {
          props.toggleEditView();
          props.actions.getElementTreeReset();
          props.setSelectedView(null);
          props.onRefresh();
          if (props.selectedView?.view_pk === props.currentViewId) {
            props.onRefreshData();
          }
        }
      });
    }
  }

  function changeScope(value) {
    if (scope !== value) {
      setUnsavedEdit(true);
      setScope(value);
    }
  }

  function toggleDefault() {
    setUnsavedEdit(true);
    setIsDefault(!isDefault);
  }

  return (
    <div className={cn('configure-view__edit-view', { 'read-only': isReadOnly })}>
      <SlideoutHeader
        closeButtonClassName="configure-view__slideout-header-close"
        doClose={props?.navigateToPrevSlideout || closeSlideout}
        headerClassName="configure-view__slideout-header"
      />

      {props.marketPricing?.saveView?.loading ? (
        <ProgressStatusOverlay
          inProgress={props.marketPricing.saveView.loading}
          primaryText={props.editMode ? 'Saving View' : 'Creating View'}
          submitSuccessDetails="This might take a moment"
        />
      ) : (
        <>
          <Link
            className="return__link"
            to={{ pathname: props.history.pathname, hash: '#configure-view' }}
            onClick={onReturn}
          >
            <i className="icon-reply" />
            Return to All Views
          </Link>

          {getTitle()}

          <div className="view-description-row">
            <div className="view-description-container">{getDescription()}</div>

            <div className="view-dropdown-container">
              {!isReadOnly ? (
                <div className="view-scope-dropdown">
                  <label>Type</label>
                  <Dropdown
                    className="view-scope-dropdown"
                    customRender={true}
                    customRenderComponent={ScopeDropdown}
                    customRenderProps={{
                      scopeDropdownItems,
                      changeScope
                    }}
                    text={getScopeText()}
                  />
                </div>
              ) : (
                <div className="view-scope-read-only">{getScopeText()}</div>
              )}

              {!isReadOnly && (
                <div className="default-scope">
                  <Toggle
                    className="default-scope-toggle"
                    id="default-scope-toggle"
                    onChange={toggleDefault}
                    value={isDefault}
                  />
                  <span className="default-scope-text">Make it Default View</span>
                </div>
              )}
            </div>
          </div>

          <div className="edit-view__elements-container">
            <ElementSelection
              categoryCode={props.categoryCode}
              pageCode={props.pageCode}
              pageName={props.pageName}
              selectedElements={selectedElements}
              toggleSelectedElement={toggleSelectedElement}
              tree={props.marketPricing?.elementTree?.data || []}
              treeLoading={props.marketPricing?.elementTree?.loading}
              treeLoaded={props.marketPricing?.elementTree?.loaded}
              isReadOnly={isReadOnly}
              elementsFilter={props.elementsFilter}
              categoryFilter={props.categoryFilter}
            />
            <ElementList
              removeElement={toggleSelectedElement}
              toggleSelectedAggType={toggleSelectedAggType}
              selectedElements={selectedElements}
              treeLoading={props.marketPricing?.elementTree?.loading}
              treeLoaded={props.marketPricing?.elementTree?.loaded}
              updateSelectedElements={data => setSelectedElements(data)}
              isReadOnly={isReadOnly}
              includeAggregates={props.includeAggregates}
              setUnsavedEdit={setUnsavedEdit}
              disableGroupInput={props.disableGroupInput}
              isGroupingEnabled={props.isGroupingEnabled}
              showAggregates={props.showAggregates}
              useOverrideLabelFormatter={props.useOverrideLabelFormatter}
              hideRequiredFields={props.hideRequiredFields}
              hideAggs={props.hideAggs}
            />
          </div>
        </>
      )}

      <SlideoutFooter isBoxShadowTheme={true}>
        <SlideoutFooterStandardButtons
          applyClassName="save-button"
          applyText="Save"
          doApply={applySaveView}
          doCancel={onReturn}
          showApply={!isReadOnly}
        />
      </SlideoutFooter>

      <Modal ref={cancelRef} className="edit-view__cancel-modal" displayCloseButton={true}>
        <React.Fragment>
          <ModalHeader className="cancel-modal__title">
            <h3 className="text">Any unsaved changes will be discarded</h3>
          </ModalHeader>
          <ModalFooter cancelBtnClick={cancelModal} submitBtnClick={submitModal} submitBtnTxtName="Continue" />
        </React.Fragment>
      </Modal>
    </div>
  );
}

export function ScopeDropdown(props) {
  return (
    <div className={cn('dropdown-views', props.className)}>
      {props.scopeDropdownItems?.map(item => {
        return (
          <div key={item.key} className="scope-item" onClick={() => props.changeScope(item.key)}>
            <div className="scope-title">{item?.title}</div>
            <div className="scope-text">{item?.text}</div>
          </div>
        );
      })}
    </div>
  );
}
